export type NavigationBlockerState = "unblocked" | "blocked" | "proceeding";

export type BlockedNavigationTarget = {
    pathname: string;
    search: string;
    hash: string;
};

/** Whether React Router should block navigation for this page. */
export function shouldBlockNavigation(when: boolean): boolean {
    return when;
}

/** When a navigation blocker activates, open the shared guard with proceed wired to blocker.proceed(). */
export function handleNavigationBlocked(
    blockerState: NavigationBlockerState,
    requestProceed: (proceed: () => void) => void,
    proceedNavigation: () => void
): void {
    if (blockerState === "blocked") {
        requestProceed(proceedNavigation);
    }
}

/** Register blocker.reset while navigation is blocked so Stay can release it. */
export function resolveBlockerResetHandler(
    blockerState: NavigationBlockerState,
    resetNavigation: () => void
): (() => void) | null {
    return blockerState === "blocked" ? resetNavigation : null;
}

/**
 * Proceed after the guard allows navigation.
 * Uses blocker.proceed() while still blocked; otherwise navigates manually to the stored target
 * (needed after save-and-continue clears dirty and the blocker auto-resets).
 */
export function proceedBlockedNavigation(options: {
    blockerState: NavigationBlockerState;
    target: BlockedNavigationTarget | null;
    proceedBlocker: () => void;
    navigateToTarget: (target: BlockedNavigationTarget) => void;
}): void {
    const { blockerState, target, proceedBlocker, navigateToTarget } = options;

    if (blockerState === "blocked") {
        proceedBlocker();
        return;
    }

    if (target) {
        navigateToTarget(target);
    }
}

export function formatBlockedNavigationPath(target: BlockedNavigationTarget): string {
    return `${target.pathname}${target.search}${target.hash}`;
}
